// src/pages/DashboardPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';

import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
  TextField,
  Divider,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
} from '@mui/material';

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
} from 'recharts';

import InventoryIcon from '@mui/icons-material/Inventory';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import {
  format,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  subDays,
  startOfQuarter,
  endOfQuarter,
} from 'date-fns';

/* ---------------- Helpers ---------------- */
const toJSDate = (v) => {
  if (!v) return null;
  try { return typeof v?.toDate === 'function' ? v.toDate() : new Date(v); } catch { return null; }
};

// Số nguyên có dấu phẩy (1,234,567)
const formatInt = (n) => Number(n || 0).toLocaleString('en-US');

// Tiền có dấu phẩy, KHÔNG thập phân, có/không hậu tố "đ"
const formatCurrency = (n, withSuffix = true) => {
  const v = Math.round(Number(n || 0));
  const s = v.toLocaleString('en-US');
  return withSuffix ? `${s} đ` : s;
};

// Hiển thị ngày Firestore
const formatDate = (ts) => {
  const d = toJSDate(ts);
  return d ? d.toLocaleString('vi-VN') : 'N/A';
};

const kpiCardSx = (from, to) => ({
  p: 2,
  height: '100%',
  borderRadius: 3,
  background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)`,
  color: '#fff',
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
});

const IconWrap = ({ children }) => (
  <Box
    sx={{
      width: 48,
      height: 48,
      borderRadius: '50%',
      bgcolor: 'rgba(255,255,255,0.25)',
      display: 'grid',
      placeItems: 'center',
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

/* ---- Tick YAxis KHÔNG WRAP (1 dòng + ellipsis) ---- */
const NoWrapYAxisTick = ({ x, y, payload }) => {
  const raw = String(payload?.value ?? '');
  const clean = raw.replace(/\s*\n+\s*/g, ' ').replace(/\s{2,}/g, ' ');
  const oneLine = clean.replace(/ /g, '\u00A0'); // NBSP để không wrap
  const MAX = 36;
  const text = oneLine.length > MAX ? oneLine.slice(0, MAX - 1) + '…' : oneLine;
  return (
    <text x={x} y={y} dy={3} textAnchor="end" fontSize={12}>
      {text}
    </text>
  );
};

function DashboardPage() {
  const [products, setProducts] = useState([]);
  const [imports, setImports] = useState([]);
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(true);

  // Preset + range
  const [preset, setPreset] = useState('thisMonth'); // today | 7days | thisMonth | thisQuarter | all
  const [startDate, setStartDate] = useState(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(endOfMonth(new Date()));

  // Top N
  const [topN, setTopN] = useState(10);
  const clampTopN = (v) => Math.min(50, Math.max(1, Number.isFinite(v) ? v : 1));

  /* ---------- Live subscriptions ---------- */
  useEffect(() => {
    const unsubProducts = onSnapshot(collection(db, 'products'), (s) =>
      setProducts(s.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    const unsubImports = onSnapshot(
      query(collection(db, 'imports'), orderBy('importDate', 'desc')),
      (s) => setImports(s.docs.map((d) => ({ ...d.data(), type: 'import', id: d.id })))
    );
    const unsubExports = onSnapshot(
      query(collection(db, 'exports'), orderBy('exportDate', 'desc')),
      (s) => setExports(s.docs.map((d) => ({ ...d.data(), type: 'export', id: d.id })))
    );
    setLoading(false);
    return () => {
      unsubProducts();
      unsubImports();
      unsubExports();
    };
  }, []);

  /* ---------- Preset -> range ---------- */
  useEffect(() => {
    const now = new Date();
    if (preset === 'today') {
      setStartDate(startOfDay(now));
      setEndDate(endOfDay(now));
    } else if (preset === '7days') {
      setStartDate(startOfDay(subDays(now, 6)));
      setEndDate(endOfDay(now));
    } else if (preset === 'thisMonth') {
      setStartDate(startOfMonth(now));
      setEndDate(endOfMonth(now));
    } else if (preset === 'thisQuarter') {
      setStartDate(startOfQuarter(now));
      setEndDate(endOfQuarter(now));
    } else if (preset === 'all') {
      setStartDate(null);
      setEndDate(null);
    }
  }, [preset]);

  /* ---------- Filter by date ---------- */
  const { filteredImports, filteredExports } = useMemo(() => {
    const start = startDate ? startOfDay(startDate) : null;
    const end = endDate ? endOfDay(endDate) : null;
    if (!start || !end) return { filteredImports: imports, filteredExports: exports };

    const fImports = imports.filter((i) => {
      const dt = toJSDate(i.importDate);
      return dt && dt >= start && dt <= end;
    });
    const fExports = exports.filter((e) => {
      const dt = toJSDate(e.exportDate);
      return dt && dt >= start && dt <= end;
    });
    return { filteredImports: fImports, filteredExports: fExports };
  }, [imports, exports, startDate, endDate]);

  /* ---------- Metrics + charts + recent ---------- */
  const { metrics, timeSeriesData, allTopProducts, recentActivity, summaryChips } = useMemo(() => {
    const totalValue = products.reduce(
      (sum, p) => sum + Number(p.quantity || 0) * Number(p.price ?? p.importPrice ?? 0),
      0
    );
    const lowStockCount = products.filter((p) => Number(p.quantity || 0) > 0 && Number(p.quantity || 0) < 10).length;
    const salesInRange = filteredExports.reduce((s, o) => s + Number(o.grandTotal || 0), 0);

    const totalImportQty = filteredImports.reduce(
      (s, o) => s + (o.items || []).reduce((x, i) => x + Number(i.quantity || 0), 0),
      0
    );
    const totalExportQty = filteredExports.reduce(
      (s, o) => s + (o.items || []).reduce((x, i) => x + Number(i.quantity || 0), 0),
      0
    );

    // Time series
    const byDay = {};
    filteredImports.forEach((imp) => {
      const d = toJSDate(imp.importDate);
      if (!d) return;
      const key = format(d, 'dd/MM');
      if (!byDay[key]) byDay[key] = { date: key, imported: 0, exported: 0, revenue: 0 };
      byDay[key].imported += (imp.items || []).reduce((s, i) => s + Number(i.quantity || 0), 0);
    });
    filteredExports.forEach((exp) => {
      const d = toJSDate(exp.exportDate);
      if (!d) return;
      const key = format(d, 'dd/MM');
      if (!byDay[key]) byDay[key] = { date: key, imported: 0, exported: 0, revenue: 0 };
      byDay[key].exported += (exp.items || []).reduce((s, i) => s + Number(i.quantity || 0), 0);
      byDay[key].revenue += Number(exp.grandTotal || 0);
    });
    const timeSeries = Object.values(byDay).sort(
      (a, b) =>
        new Date(a.date.split('/').reverse().join('-')) -
        new Date(b.date.split('/').reverse().join('-'))
    );

    // Top SP
    const qtyMap = new Map();
    filteredExports.forEach((o) =>
      (o.items || []).forEach((it) =>
        qtyMap.set(it.productName, (qtyMap.get(it.productName) || 0) + Number(it.quantity || 0))
      )
    );
    const fullTop = Array.from(qtyMap.entries())
      .map(([name, quantity]) => ({ name, quantity }))
      .sort((a, b) => b.quantity - a.quantity);

    // Recent (all)
    const combined = [
      ...imports.map((i) => ({ ...i, date: i.importDate })),
      ...exports.map((e) => ({ ...e, date: e.exportDate })),
    ];
    const recent = combined
      .sort((a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0))
      .slice(0, 7);

    const chips = [
      { label: `Đơn nhập: ${filteredImports.length}`, color: 'info' },
      { label: `Đơn xuất: ${filteredExports.length}`, color: 'success' },
      { label: `SL nhập: ${formatInt(totalImportQty)}`, color: 'default' },
      { label: `SL xuất: ${formatInt(totalExportQty)}`, color: 'default' },
      { label: `Doanh thu: ${formatCurrency(salesInRange)}`, color: 'primary' },
    ];

    return {
      metrics: {
        productCount: products.length,
        totalValue: formatCurrency(totalValue),
        lowStockCount,
        salesInRange: formatCurrency(salesInRange),
      },
      timeSeriesData: timeSeries,
      allTopProducts: fullTop,
      recentActivity: recent,
      summaryChips: chips,
    };
  }, [products, filteredImports, filteredExports, imports, exports]);

  const topSellingProducts = useMemo(
    () => allTopProducts.slice(0, clampTopN(topN)),
    [allTopProducts, topN]
  );

  // === Chart Top tự giãn theo số lượng + nới trục Y theo tên dài nhất ===
  const topChartHeight = useMemo(() => {
    const n = topSellingProducts.length || 1;
    const perBar = 28; // px/ thanh
    const minH = 320;
    const maxH = 1400;
    return Math.min(maxH, Math.max(minH, n * perBar));
  }, [topSellingProducts]);

  const yAxisWidth = useMemo(() => {
    const maxLen = topSellingProducts.reduce((m, p) => Math.max(m, (p.name || '').length), 0);
    return Math.min(320, Math.max(120, maxLen * 7)); // 7px/ ký tự + đệm
  }, [topSellingProducts]);

  if (loading) return <Typography>Đang tải Dashboard...</Typography>;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Bảng điều khiển</Typography>
          <Typography variant="body2" color="text.secondary">
            Theo dõi tồn kho, doanh thu và hoạt động nhập/xuất theo thời gian thực
          </Typography>
        </Box>

        {/* Preset + chọn ngày */}
        <Paper sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 3 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={preset}
            onChange={(_, v) => v && setPreset(v)}
          >
            <ToggleButton value="today">Hôm nay</ToggleButton>
            <ToggleButton value="7days">7 ngày qua</ToggleButton>
            <ToggleButton value="thisMonth">Tháng này</ToggleButton>
            <ToggleButton value="thisQuarter">Quý này</ToggleButton>
            <ToggleButton value="all">Tất cả</ToggleButton>
          </ToggleButtonGroup>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Từ ngày"
              value={startDate}
              onChange={(v) => { setStartDate(v); setPreset(''); }}
              renderInput={(params) => <TextField {...params} size="small" sx={{ minWidth: 160 }} />}
            />
            <DatePicker
              label="Đến ngày"
              value={endDate}
              onChange={(v) => { setEndDate(v); setPreset(''); }}
              renderInput={(params) => <TextField {...params} size="small" sx={{ minWidth: 160 }} />}
            />
          </LocalizationProvider>
        </Paper>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={kpiCardSx('#34d399', '#059669')}>
            <IconWrap><MonetizationOnIcon htmlColor="#fff" /></IconWrap>
            <Box>
              <Typography variant="overline" sx={{ opacity: 0.85 }}>Tổng giá trị kho</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{metrics.totalValue}</Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={kpiCardSx('#60a5fa', '#2563eb')}>
            <IconWrap><InventoryIcon htmlColor="#fff" /></IconWrap>
            <Box>
              <Typography variant="overline" sx={{ opacity: 0.85 }}>Tổng loại sản phẩm</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{formatInt(metrics.productCount)}</Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={kpiCardSx('#f59e0b', '#d97706')}>
            <IconWrap><ShoppingCartIcon htmlColor="#fff" /></IconWrap>
            <Box>
              <Typography variant="overline" sx={{ opacity: 0.85 }}>Doanh thu (đã lọc)</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{metrics.salesInRange}</Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={kpiCardSx('#f87171', '#ef4444')}>
            <IconWrap><WarningAmberIcon htmlColor="#fff" /></IconWrap>
            <Box>
              <Typography variant="overline" sx={{ opacity: 0.85 }}>Sản phẩm sắp hết</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{formatInt(metrics.lowStockCount)}</Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Summary chips */}
      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {summaryChips.map((c, idx) => (
            <Chip key={idx} color={c.color} label={c.label} variant={c.color === 'default' ? 'outlined' : 'filled'} />
          ))}
        </Box>
      </Paper>

      {/* Charts: xếp dọc bằng Stack */}
      <Stack direction="column" spacing={2} sx={{ mb: 2 }}>
        {/* HÀNG 1: Nhập / Xuất & Doanh thu */}
        <Paper sx={{ p: 2, borderRadius: 3, height: { xs: 420, md: 460 } }}>
          <Typography variant="h6" gutterBottom>
            Nhập / Xuất & Doanh thu theo ngày
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <ResponsiveContainer width="100%" height="85%">
            <ComposedChart data={timeSeriesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" tickFormatter={formatInt} />
              <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => formatCurrency(v, false)} />
              <RechartsTooltip
                formatter={(v, n) => [
                  n === 'revenue' ? formatCurrency(v) : formatInt(v),
                  n === 'revenue' ? 'Doanh thu' : (n === 'imported' ? 'Nhập' : 'Xuất'),
                ]}
              />
              <Legend />
              <Bar yAxisId="left" dataKey="imported" name="Nhập" fill="#60a5fa" />
              <Bar yAxisId="left" dataKey="exported" name="Xuất" fill="#34d399" />
              <Line yAxisId="right" type="monotone" dataKey="revenue" name="Doanh thu" stroke="#f59e0b" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </Paper>

        {/* HÀNG 2: Top SP bán chạy (tự giãn chiều cao + NHÃN KHÔNG WRAP) */}
        <Paper sx={{ p: 2, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6">Top sản phẩm bán chạy</Typography>
            <Box sx={{ width: 220, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">Top</Typography>
              <Slider
                size="small"
                value={topN}
                onChange={(_, v) => setTopN(clampTopN(Array.isArray(v) ? v[0] : v))}
                min={1}
                max={50}
                valueLabelDisplay="auto"
              />
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ height: topChartHeight }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={topSellingProducts}
                layout="vertical"
                margin={{ top: 8, right: 12, left: 12, bottom: 8 }}
                barCategoryGap={6}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={formatInt} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={yAxisWidth}
                  interval={0}
                  tick={<NoWrapYAxisTick />}
                />
                <RechartsTooltip formatter={(v) => [formatInt(v), 'Số lượng']} />
                <Bar dataKey="quantity" name="Đã xuất" fill="#a78bfa" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Stack>

      {/* Recent activity */}
      <Paper sx={{ p: 2, borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom>Hoạt động gần đây</Typography>
        <Divider sx={{ mb: 1.5 }} />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Loại</TableCell>
                <TableCell>Thời gian</TableCell>
                <TableCell>Chi tiết</TableCell>
                <TableCell align="right">Tổng SL</TableCell>
                <TableCell>Người tạo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentActivity.map((act) => {
                const totalQty = (act.items || []).reduce((s, it) => s + Number(it.quantity || 0), 0);
                return (
                  <TableRow key={act.id} hover>
                    <TableCell>
                      {act.type === 'import'
                        ? <Chip label="NHẬP" color="info" size="small" variant="outlined" />
                        : <Chip label="XUẤT" color="success" size="small" variant="outlined" />}
                    </TableCell>
                    <TableCell>{formatDate(act.date)}</TableCell>
                    <TableCell>
                      {act.type === 'import'
                        ? `PO: ${act.poNumber || ''} · NCC: ${act.supplierName || ''}`
                        : `SO: ${act.soNumber || ''} · KH: ${act.customerName || ''}`}
                    </TableCell>
                    <TableCell align="right">{formatInt(totalQty)}</TableCell>
                    <TableCell>{act.createdBy || ''}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default DashboardPage;
